/**
 * The action waiting on a step-up sign-in.
 *
 * Payroll approval, payment and payout-number changes need a recent sign-in
 * (`403 STEP_UP_REQUIRED`). The action is parked here (sessionStorage: this
 * tab only, gone when it closes), KorOS sign-in runs with `prompt=login` and
 * `max_age=0`, and back in the app the person confirms before it is resumed —
 * a payment is never re-submitted without them seeing it.
 *
 * Kept free of session/sign-in imports so the callback can mark it.
 */
import { readJson, readStorage, removeStorage, writeJson } from '@/lib/storage';

const KEY = 'kb.stepup.pending';

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

/** For `useSyncExternalStore`: notified whenever the parked action changes. */
export function subscribeStepUp(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Raw stored value (a stable string snapshot for `useSyncExternalStore`). */
export function stepUpSnapshot(): string | null {
  return readStorage(KEY, 'session');
}
/** A parked action older than this is dropped rather than resumed. */
export const STEP_UP_MAX_AGE_MS = 15 * 60_000;

export type PendingStepUp = {
  /** Registered action name, e.g. `payroll.approve`. */
  action: string;
  /** What the handler needs (ids, the new payout number…). */
  params: Record<string, string>;
  /** Where to come back to after signing in. */
  route: string;
  /** What the person will be asked to confirm, already translated. */
  label: string;
  /** Who parked it: never resumed for a different person. */
  user_id: string;
  created_at: number;
  /** Set when the sign-in this action waited for has completed. */
  signed_in: boolean;
};

export function savePendingStepUp(
  pending: Omit<PendingStepUp, 'created_at' | 'signed_in'>,
  now: number = Date.now(),
): PendingStepUp {
  const record: PendingStepUp = { ...pending, created_at: now, signed_in: false };
  writeJson(KEY, record, 'session');
  notify();
  return record;
}

export function readPendingStepUp(now: number = Date.now()): PendingStepUp | null {
  const pending = readJson<PendingStepUp>(KEY, 'session');
  if (!pending) return null;
  if (now - pending.created_at > STEP_UP_MAX_AGE_MS) {
    removeStorage(KEY, 'session');
    notify();
    return null;
  }
  return pending;
}

export function clearPendingStepUp(): void {
  removeStorage(KEY, 'session');
  notify();
}

/** Called when a sign-in completes: a parked action may now be resumed. */
export function markStepUpSignedIn(): void {
  const pending = readPendingStepUp();
  if (!pending) return;
  writeJson(KEY, { ...pending, signed_in: true }, 'session');
  notify();
}

/** Extra authorize parameters that force KorOS to ask for the password again. */
export const STEP_UP_AUTHORIZE_PARAMS = { prompt: 'login', max_age: '0' } as const;
